import { DEFAULT_ENTITY_MAP_FILTER_STATE, type EntityMapFilterState } from './entityMapFilters';
import { validateEntityMapFilterState } from './entityMapFilterPersistence';
import type { EntityToggleState, FeatureOverlayKey, FeatureOverlayState } from './mapLayers';
import { ENTITY_TOGGLES, FEATURE_OVERLAYS, POI_TYPES } from './mapLayers';

// Split-model URL codecs. The `filters` param moves to a `v2:` prefix carrying the
// EntityMapFilterState diff; a pre-split `v1:` value falls through the unknown-prefix path
// to null exactly as any unknown version always has (caller forces filters inactive — safe
// degradation for stale links). The `layers` param gains the `pois`/`leaks` entity tokens.

// -- Filters --

/**
 * Serialises the split-model filter state for the URL `filters` param: '0' when inactive,
 * otherwise `v2:<diff-json>` carrying only the non-default fields.
 */
export function serializeEntityFiltersToUrl(isActive: boolean, committed: EntityMapFilterState): string {
    if (!isActive) return '0';

    const defaults = DEFAULT_ENTITY_MAP_FILTER_STATE;
    const payload: Record<string, unknown> = {};

    if (committed.show !== defaults.show) payload['show'] = committed.show;
    if (committed.dateRange !== defaults.dateRange) payload['dateRange'] = committed.dateRange;
    if (committed.customDateRange.from !== null || committed.customDateRange.to !== null) {
        payload['customDateRange'] = committed.customDateRange;
    }
    if (committed.leakVisibility !== defaults.leakVisibility) payload['leakVisibility'] = committed.leakVisibility;

    const diffToggles = (defaultsMap: Record<string, boolean>, committedMap: Record<string, boolean>) => {
        const diff: Record<string, boolean> = {};
        for (const key of Object.keys(defaultsMap)) {
            if (committedMap[key] !== defaultsMap[key]) diff[key] = committedMap[key];
        }
        return diff;
    };

    const poiDiff = diffToggles(defaults.poiStatuses, committed.poiStatuses);
    if (Object.keys(poiDiff).length > 0) payload['poiStatuses'] = poiDiff;

    const leakDiff = diffToggles(defaults.leakStatuses, committed.leakStatuses);
    if (Object.keys(leakDiff).length > 0) payload['leakStatuses'] = leakDiff;

    const pipeDiff = diffToggles(defaults.pipeTypes, committed.pipeTypes);
    if (Object.keys(pipeDiff).length > 0) payload['pipeTypes'] = pipeDiff;

    return `v2:${JSON.stringify(payload)}`;
}

/**
 * Parses the raw `filters` URL param: '0' → explicit inactive; valid `v2:` → active with
 * a field-repaired state; anything else (including pre-split `v1:` payloads) → null, which
 * the caller treats as forced-inactive.
 */
export function deserializeEntityFiltersFromUrl(
    raw: string,
): { isActive: boolean; committed: EntityMapFilterState } | null {
    if (raw === '0') return { isActive: false, committed: validateEntityMapFilterState(undefined) };
    if (!raw.startsWith('v2:')) return null;
    try {
        const parsed: unknown = JSON.parse(raw.slice(3));
        return { isActive: true, committed: validateEntityMapFilterState(parsed) };
    } catch {
        return null;
    }
}

// -- Layers --

const FEATURE_OVERLAY_KEYS: ReadonlyArray<FeatureOverlayKey> = FEATURE_OVERLAYS.map((d) => d.key);
const ENTITY_TOGGLE_KEYS = ENTITY_TOGGLES.map((d) => d.key);
const LEGACY_POI_TYPE_KEYS = new Set<string>(POI_TYPES.map((d) => d.key));

/** Joins visible overlay keys and entity-toggle keys with commas. Empty string = all off. */
export function serializeEntityLayersToUrl(
    featureOverlays: FeatureOverlayState,
    entityToggles: EntityToggleState,
): string {
    const visible: string[] = [];
    for (const key of FEATURE_OVERLAY_KEYS) {
        if (featureOverlays[key]) visible.push(key);
    }
    for (const key of ENTITY_TOGGLE_KEYS) {
        if (entityToggles[key]) visible.push(key);
    }
    return visible.join(',');
}

/**
 * Parses a comma-separated `layers` param. A present param is authoritative (absent keys
 * mean off) — EXCEPT for pre-split URLs: any legacy per-status token
 * (leakFound/noLeakFound/…) marks the value as v1-era, where entity tokens could not
 * exist, so the entity toggles fall back to their both-on defaults and only the overlay
 * tokens are honoured. Without this rule a stale `layers=districtMeterAreas,leakFound`
 * link would render an empty map.
 */
export function deserializeEntityLayersFromUrl(param: string): {
    featureOverlays: FeatureOverlayState;
    entityToggles: EntityToggleState;
} {
    const parts = new Set(
        param
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
    );

    const featureOverlays = Object.fromEntries(
        FEATURE_OVERLAY_KEYS.map((key) => [key, parts.has(key)]),
    ) as FeatureOverlayState;

    const isLegacyUrl = [...parts].some((part) => LEGACY_POI_TYPE_KEYS.has(part));
    const entityToggles: EntityToggleState = isLegacyUrl
        ? { pois: true, leaks: true }
        : { pois: parts.has('pois'), leaks: parts.has('leaks') };

    return { featureOverlays, entityToggles };
}
